"use client";
import { captureMessage } from "@sentry/nextjs";
import { signIn, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { RxCheck } from "react-icons/rx";
import Spinner from "~/components/themed/spinner";
import { If } from "~/components/v3/Conditional";
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "~/components/v3/shadcn/InputOTP";
import { RSVPStepsFormInputData } from "../validators";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";

// For whomever comes next:
// This component introduces a few hacks because NextAuth doesn't
// support OTPs for SMS.
const QuickRegisterVerifyCodeModalContent = ({
    setNextDisabled,
    onFinish,
    customization,
}: {
    setNextDisabled: (disabled: boolean) => void;
    onFinish: () => void;
    customization?: CustomizableLayout;
}) => {
    const { status, update, data } = useSession();
    const router = useRouter();
    const statusRef = useRef(status);

    const [isLoading, setIsLoading] = useState(false);
    const [otp, setOtp] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [finishedRsvp, setFinishedRsvp] = useState(false);

    const className = cn(
        "text-md w-12 h-12 md:w-14 md:h-14 md:text-xl",
        customization?.defaultTheme === "light"
            ? "text-black border-black"
            : "text-white",
    );

    const { getValues, watch } = useFormContext<RSVPStepsFormInputData>();
    const email = watch("register.email");
    const phone = watch("register.phone");
    const countryCodeData = watch("register.countryCode");
    const contactMethod = watch("register.contactMethod") || "email";

    useEffect(() => {
        const sendVerification = async () => {
            setIsLoading(true);
            const identifier = contactMethod === "email" ? email : phone;
            if (!identifier) return;

            await signIn(
                contactMethod === "email"
                    ? "magic-code-login"
                    : "sms-magic-code-login",
                {
                    email:
                        contactMethod === "email"
                            ? identifier.toLowerCase()
                            : undefined,
                    phone: contactMethod === "sms" ? identifier : undefined,
                    countryCode:
                        contactMethod === "sms" ? countryCodeData : undefined,
                    name: getValues("register.name"),
                    redirect: false,
                },
            );

            setIsLoading(false);
        };
        sendVerification();
    }, [contactMethod]);

    useEffect(() => {
        statusRef.current = status;
        setNextDisabled(status !== "authenticated");
    }, [status]);

    useEffect(() => {
        setErrorMessage("");
        const checkOtp = async () => {
            if (otp.length === 6) {
                setIsLoading(true);
                let identifier;
                if (contactMethod === "email") {
                    identifier = email;
                } else {
                    identifier = phone;
                }
                const countryCode =
                    contactMethod === "sms" ? countryCodeData : undefined;
                if (!identifier) return;
                const isValid = await verifyOTP(
                    otp,
                    identifier.toLowerCase(),
                    countryCode,
                );
                if (isValid) await refreshSession();
                else {
                    setIsLoading(false);
                    setErrorMessage("Error verifying passcode");
                    return;
                }
                // refetch the modal
                onFinish();
                setIsLoading(false);
                setFinishedRsvp(true);
                router.refresh();
            }
        };
        checkOtp();
    }, [otp, contactMethod]);

    useEffect(() => {
        if (status === "authenticated" && !data.user.name)
            update({ name: getValues("register.name") });
    }, [status, data]);

    const verifyOTP = async (
        otp: string,
        identifier: string,
        countryCode?: string,
    ) => {
        if (contactMethod === "email") {
            const resp = await fetch(
                `/api/auth/callback/email?email=${encodeURIComponent(identifier)}&token=${otp}`,
            );
            // no error in URL etc
            return resp.status === 200 && !resp.url.includes("error");
        } else {
            // Yes, we repeat the signIn call here
            // so we can create a valid session
            const resp = await signIn("sms-magic-code-login", {
                phone,
                countryCode,
                name: getValues("register.name"),
                token: otp,
                redirect: false,
            });
            return resp?.status === 200 && !resp?.url?.includes("error");
        }
    };

    const refreshSession = async () => {
        // total hack but you can learn more here if you want
        // - https://github.com/nextauthjs/next-auth/discussions/4229
        // - https://github.com/nextauthjs/next-auth/issues/596
        // FUN STUFF
        const newValue = JSON.stringify({
            event: "session",
            data: { trigger: "getSession" },
            timestamp: Math.floor(Date.now() / 1000),
        });
        localStorage.setItem("nextauth.message", newValue);
        window.dispatchEvent(
            new StorageEvent("storage", {
                key: "nextauth.message",
                newValue,
                storageArea: localStorage,
            }),
        );

        // if no session update in 5s, reload the page (extreme case fallback)
        let start = Date.now();
        await new Promise((resolve) =>
            setTimeout(async function run() {
                if (statusRef.current === "authenticated") return resolve(true);
                if (Date.now() - start > 5000) {
                    // reload the page and log in sentry
                    console.warn(
                        "Unable to refresh the session",
                        statusRef.current,
                    );
                    captureMessage("Unable to refresh the session");
                    window.location.reload();
                    return resolve(false);
                } else setTimeout(run, 200);
            }, 100),
        );
    };

    return (
        <div className="flex flex-col items-center gap-4">
            <div className="items-center space-y-2">
                <div className="overflow-hidden">
                    <InputOTP
                        maxLength={6}
                        value={otp}
                        onChange={(value) => setOtp(value)}
                        disabled={
                            isLoading ||
                            (status === "authenticated" && finishedRsvp)
                        }
                        containerClassName="has-[:disabled]:opacity-20"
                    >
                        <InputOTPGroup>
                            <InputOTPSlot className={className} index={0} />
                            <InputOTPSlot className={className} index={1} />
                            <InputOTPSlot className={className} index={2} />
                            <InputOTPSlot className={className} index={3} />
                            <InputOTPSlot className={className} index={4} />
                            <InputOTPSlot className={className} index={5} />
                        </InputOTPGroup>
                    </InputOTP>

                    <If condition={status === "authenticated" && finishedRsvp}>
                        <RxCheck className="absolute left-1/2 top-1/2 h-14 w-12 -translate-x-1/2 -translate-y-1/2 transform text-center text-v3-primary" />
                    </If>
                </div>
                <div
                    className={cn(
                        "flex flex-col items-center justify-center gap-2 text-center text-xs",
                        customization?.descriptionClassName,
                    )}
                >
                    <If condition={!otp && !isLoading}>
                        Enter one-time password from your{" "}
                        {contactMethod === "email" ? "email" : "sms"} inbox.
                    </If>

                    <If condition={!!errorMessage}>
                        <span className="text-red-500">{errorMessage}</span>
                    </If>
                    <If condition={isLoading}>
                        <Spinner type="small" className="h-6 w-6" />
                    </If>
                </div>
            </div>
        </div>
    );
};

export default QuickRegisterVerifyCodeModalContent;
